import datapoints from './datapoints';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { TriageContext, userMailInteractionAction } from 'owa-mail-actions';
import markConversationsAsPinnedStoreUpdate from 'owa-mail-actions/lib/triage/markConversationsAsPinnedStoreUpdate';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import * as undoActions from 'owa-mail-undo';
import {
    filterConversationRowsByRestriction,
    getIsTableConversationRelationPinned,
    getTableConversationRelation,
    TableView,
    TableViewConversationRelation,
    MailRowDataPropertyGetter,
    listViewStore,
} from 'owa-mail-list-store';
import { getApolloClient } from 'owa-apollo';
import { MailboxInfoInput } from 'owa-graph-schema';
import {
    PinConversationDocument,
    PinConversationMutation,
} from '../graphql/__generated__/pinConversationMutation.interface';

type PinConversationResult = PinConversationMutation['pinConversation'];

const invokePinConversationMutationFn = async function invokePinConversationMutation(
    conversationIds: string[],
    itemIds: string[],
    mailboxInfo: MailboxInfoInput,
    shouldPin: boolean,
    folderId: string
): Promise<PinConversationResult | undefined> {
    const conversationSnapShots = conversationIds.map(conversationId => {
        return {
            id: conversationId,
        };
    });

    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            itemIds: itemIds,
            mailboxInfo: mailboxInfo,
            shouldPin: shouldPin,
            folderId: folderId,
        },
        mutation: PinConversationDocument,
    });
    return result?.data?.pinConversation;
};

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokePinConversationMutation: invokePinConversationMutationFn,
};

function getItemIdsToUpdate(
    rowKeys: string[],
    tableViewId: string,
    shouldMarkAsPinned: boolean
): string[] {
    const itemIdsToUpdate: { [id: string]: boolean } = {};

    for (let i = 0; i < rowKeys.length; i++) {
        const tableConversationRelation = getTableConversationRelation(rowKeys[i], tableViewId);

        if (shouldMarkAsPinned) {
            // If pinning, pin the last item in each conversation. The last delivered item is first in the list of ItemIds
            const itemId = tableConversationRelation.itemIds[0];
            if (!itemIdsToUpdate[itemId]) {
                itemIdsToUpdate[itemId] = true;
            }
        } else {
            // If unpinning, unpin all local items in conversation.
            tableConversationRelation.itemIds.forEach(itemId => {
                if (!itemIdsToUpdate[itemId]) {
                    itemIdsToUpdate[itemId] = true;
                }
            });
        }
    }

    return Object.keys(itemIdsToUpdate);
}

function shouldPerformPinUnpinAction(
    tableConversationRelation: TableViewConversationRelation,
    pin: boolean
): boolean {
    return (
        tableConversationRelation &&
        getIsTableConversationRelationPinned(tableConversationRelation) != pin
    );
}

export default createLegacyActionWithDatapoint(
    'pinConversations',
    datapoints.pinConversations
)(async function pinConversations(
    conversationRowKeys: string[],
    tableView: TableView,
    shouldMarkAsPinned: boolean,
    actionSource: ActionSource
): Promise<any> {
    await undoActions.clearLastUndoableAction();

    const filteredRowKeys = filterConversationRowsByRestriction(
        conversationRowKeys,
        tableView.id,
        shouldPerformPinUnpinAction,
        shouldMarkAsPinned
    );

    const conversationId = MailRowDataPropertyGetter.getConversationId(
        filteredRowKeys?.[0],
        tableView
    );

    const itemIdsToUpdate = getItemIdsToUpdate(filteredRowKeys, tableView.id, shouldMarkAsPinned);

    if (itemIdsToUpdate.length == 0) {
        // No need to send out the service call
        return Promise.resolve();
    }

    // Automatically increment how many items to render to account for extra pinned items
    listViewStore.lastIndexToRender += itemIdsToUpdate.length;

    markConversationsAsPinnedStoreUpdate(filteredRowKeys, tableView.id, shouldMarkAsPinned);

    const instrumentationContexts = getInstrumentationContextsFromTableView(
        conversationRowKeys,
        tableView
    );

    const triageContext: TriageContext = {
        itemIds: itemIdsToUpdate,
        conversationIds: [conversationId],
    };

    userMailInteractionAction(
        shouldMarkAsPinned ? 'Pin' : 'Unpin',
        instrumentationContexts,
        triageContext
    );

    const mailboxInfo = getMailboxInfo(tableView);

    // Make service request
    // Task 658: Reload client state when pin action fails on the server
    return exportedHelperFunctions.invokePinConversationMutation(
        [conversationId],
        itemIdsToUpdate,
        mailboxInfo,
        shouldMarkAsPinned,
        tableView.tableQuery.folderId
    );
});
